/** Small live pieces of the landing: a number that counts to its value, and the tape of last trades. */
import { useEffect, useRef, useState } from "react";

const still = () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Counts from the previous value (or zero) to `value` with an expo ease; shows `format(value)` without script. */
export function CountUp({ value, format }: { value: number | null | undefined; format: (n: number) => string }) {
  const [shown, setShown] = useState<number | null>(value ?? null);
  const from = useRef(0);
  useEffect(() => {
    if (value == null) return;
    if (still()) {
      setShown(value);
      return;
    }
    const start = from.current;
    const t0 = performance.now();
    const dur = Math.abs(value - start) < 1 ? 0 : 1400;
    let raf = 0;
    const step = (now: number) => {
      const k = dur ? Math.min(1, (now - t0) / dur) : 1;
      const eased = 1 - 2 ** (-10 * k);
      setShown(start + (value - start) * (k === 1 ? 1 : eased));
      if (k < 1) raf = requestAnimationFrame(step);
      else from.current = value;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{shown == null ? "—" : format(shown)}</>;
}

export type TapeItem = { id: string; label: string; price: string; dir: "up" | "down" | "" };

/** An endless band of last prices (the list is rendered twice so the loop is seamless); pauses on hover. */
export function LiveTape({ items, label }: { items: TapeItem[]; label: string }) {
  if (!items.length) return null;
  const row = (copy: number) =>
    items.map((it) => (
      <li key={`${copy}-${it.id}`} aria-hidden={copy > 0 || undefined}>
        <span className="tape-sym">{it.label}</span>
        <span className={`tape-px ${it.dir}`}>{it.price}</span>
        <span className={`tape-arrow ${it.dir}`} aria-hidden="true">
          {it.dir === "down" ? "▼" : "▲"}
        </span>
      </li>
    ));
  return (
    <section className="tape" aria-label={label}>
      <ul>
        {row(0)}
        {row(1)}
        {row(2)}
      </ul>
    </section>
  );
}
